import { type WorkshopBusinessId } from '@icare/types';

import { type MasterDataStore } from '../masterdata/masterdata.type';
import { type InterventionStatus } from '../../business/interventionButtonMenu.const';
import { type FilterType } from '../../components/Filters/filter.type';
import { type OrderRaw } from '../../pages/SparePartOrders/types';
import { type NullableDate } from '../../types/globals';
import { type StoreIdAndTaxesCondition } from '../../store/selectors/price';
import { type BOOKING_REFERENCE_TYPE } from '../../features/booking/constants/reference';
import { type SKU_REFERENCE_TYPE } from '../../features/marketplace/constants/reference';

export enum InterventionType {
  standard = 1,
  homeService = 3,
  insurance = 4,
  express = 5,
  web = 6,
  internal = 7,
}

export interface FooterInfos {
  state: InterventionState;
  pledgeDate: NullableDate;
  docNumber?: string;
  hasInvoice?: boolean;
  totalTheoreticalTime?: number;
}

export interface RealizationSite extends MasterDataStore {
  delay?: number;
}

export interface ServiceWorkshopRaw {
  cession_price: number;
  item_code: number;
  label?: string;
  model_code: number;
  pogo: string;
  pogo_id: number;
  quantity: number;
  theoretical_time: number;
  unit_price_without_taxes: number;
  theoretical_unit_price_without_taxes?: number;
  unit_price_with_taxes: number;
  theoretical_unit_price_with_taxes?: number;
}

export interface ServiceWorkshop {
  cessionPrice: number;
  itemCode: number;
  label: string;
  modelCode: number;
  pogo: string;
  pogoId: number;
  quantity: number;
  theoreticalTime: number;
  unitPriceWithoutTaxes: number;
  theoreticalUnitPriceWithoutTaxes?: number;
  unitPriceWithTaxes: number;
  theoreticalUnitPriceWithTaxes?: number;
}

interface OrderWorkshopRaw {
  order_number: number;
  status: string;
  warehouse?: number;
  delivery_date?: string;
  received_date?: string;
}

export interface SparePartWorkshopRaw {
  item_code: number;
  order_in_progress: boolean;
  pogo: string;
  pogo_id: number;
  quantity: number;
  unit_price_with_taxes: number;
  theoretical_unit_price_with_taxes?: number;
  unit_price_without_taxes: number;
  theoretical_unit_price_without_taxes?: number;
  wacp_price: number;
  orders: OrderWorkshopRaw[];
  stock_movement_quantity: number;
  order_numbers?: number[];
  model_code?: number;
}

export interface SparePartWorkshop {
  itemCode: number;
  orderInProgress: boolean;
  pogo: string;
  pogoId: number;
  quantity: number;
  unitPriceWithTaxes: number;
  theoreticalUnitPriceWithTaxes?: number;
  unitPriceWithoutTaxes: number;
  theoreticalUnitPriceWithoutTaxes?: number;
  wacpPrice: number;
  orders: OrderRaw[];
  stockMovementQuantity: number;
  orderNumbers: number[];
  label?: string;
  modelCode?: number;
}

export interface InterventionEventFromSearch {
  date: string;
  eventId: InterventionsEventsType;
}

export interface WorkshopIntervention {
  stateId: number;
  stateLabel?: string;
  documentNumber: string;
  invoiceNumber?: string;
  isLocal: boolean;
  isWarranty?: boolean;
  creationSite: number;
  realisationSite: number;
  creationDate: Date;
  interventionType: InterventionType;
  customerLanguage: string;
  depositedProduct: DepositedProduct;
  totalTheoreticalTime: number;
  currencyCode: string;
  familyLabel?: string;
  familyId: number;
  services?: ServiceWorkshop[];
  spareParts?: SparePartWorkshop[];
  customerAlertType: string;
  availabilityDate?: NullableDate;
  contextId?: WorkshopBusinessId;
  memberId?: string;
  displayName?: string;
  storeComment?: string;
  workshopComment?: string;
  traceabilityNumber?: string;
  stringTension?: string;
  externalReferenceNumber?: string;
  maxOgeaAmount?: number;
  validateTotalPrice?: number;
  totalPriceWithTaxes?: number;
  events?: InterventionEventFromSearch[];
}

export interface WorkshopInterventionRaw {
  services: ServiceWorkshopRaw[];
  document_number: string;
  is_local: boolean;
  creation_site: number;
  realisation_site: number;
  creation_date: string;
  intervention_type: number;
  context_id: WorkshopBusinessId;
  customer_language: string;
  deposited_product?: DepositedProductRaw;
  total_theoretical_time: number;
  total_price_with_taxes: number;
  currency_code: string;
  family_id: number;
  spare_parts: SparePartWorkshopRaw[];
  max_ogea_amount: number;
  state_label: string;
  state_id: number;
  member_id: string;
  customer_alert_type: 'BOTH' | 'EMAIL' | 'PHONE_NUMBER' | 'NONE';
  availability_date: string;
}

interface InterventionStateRaw {
  state_id: number;
  state_label?: string;
}

interface InterventionDocument {
  fileContent: string;
  fileName: string;
}

export interface InterventionFiles {
  names: string[];
  list?: FileList | null;
}

export interface InterventionState {
  stateId: InterventionStatus;
  stateLabel?: string;
  document?: InterventionDocument;
}

export interface GetInterventionsIdsParams {
  toDoEvents?: ToDoEvent[];
  processes?: number[];
  fromDate?: Date;
  toDate?: Date;
  toSelf?: boolean;
  nextParams?: string;
}

interface DepositedProductRaw {
  item_code?: number;
  category_id?: number;
  purchase_date?: string;
}

interface DepositedProduct {
  itemCode?: number;
  modelCode?: number;
  label?: string;
  imageUrl?: string;
  purchaseDate?: Date;
  location?: string | null;
  processId?: number | null;
  categoryId?: number | null;
  natureId?: number;
  sportIds?: number[];
  groupSportId?: number;
  groupNatureLevelOneId?: number;
  groupNatureLevelTwoId?: number;
  groupNatureLevelThreeId?: number;
  structuredCharacteristicIds?: number[];
}

export interface InterventionProduct extends Omit<DepositedProduct, 'purchaseDate'> {
  purchaseDate?: string;
  isBatteryChecked: boolean | null;
}

export interface PatchInterventionProduct extends Omit<InterventionProduct, 'isBatteryChecked'> {
  isBatteryChecked?: boolean;
}

export interface CreationResponseRaw extends ServicesResponse {
  document_number: string;
  state: InterventionStateRaw;
}

export interface InterventionCreationResponse {
  documentNumber: string;
  state: {
    stateId: number;
  };
}

interface ServicesResponse {
  services?: {
    services: ServiceWorkshop[];
  };
}

export interface CreationResponse {
  docNumber: string;
  state: InterventionState;
  reload?: boolean;
  errorMessages?: string[];
}

export interface RawShippingReturn {
  interventions: [
    {
      document_number: string;
      state: {
        state_id: number;
        state_label: string;
      };
    },
  ];
  parcel_number: string;
}

export interface ShippingResponse extends CreationResponse {
  parcelNumber: string;
}

export enum PDFType {
  invoice = 'invoice',
  order = 'order',
  red = 'red',
  quote = 'quote',
}

export interface Delay {
  familyId: number;
  delay: number;
}

export type StateParam = '/refuse' | '/delete' | '';

export const enum InterventionsEventsType {
  ITV_CREATION_AMOUNT_DATE = 1,
  ITV_VALIDATION_DATE = 4,
  ITV_SENT_TO_AR_DATE = 5,
  ITV_SENT_TO_STORE_DATE = 6,
  ITV_INVOICE_CREATION_DATE = 7,
  ITV_CLOTURE_DATE = 8,
  ITV_AVAILABLE_DATE = 9,
  ITV_RECEPTION_AR_DATE = 10,
  ITV_CHECK_OUT_DATE = 11,
  ITV_DELETE_DATE = 12,
  STORE_STOCK_SHORTAGE = 13,
  RW_STOCK_SHORTAGE = 14,
  ITV_REFUSAL_CUSTOMER_DATE = 15,
  ITV_SHIPMENT_CANCEL = 16,
  STORE_SEND_ITV_TO_PARTNER = 17,
  STORE_RECEIVE_ITV_FROM_PARTNER = 18,
  RW_SEND_ITV_TO_PARTNER = 19,
  RW_RECEIVE_ITV_FROM_PARTNER = 20,
}

interface DelayDetail {
  familyId: number;
  delay: number;
}

export interface RegionalWorkshopDelay {
  localWorkshop: number;
  delayDetails: DelayDetail[];
}

export interface WorkshopDelay {
  familyId: number;
  delay: number;
  localWorkshop: number;
}

export interface ToDoEvent {
  date: Date;
  interventionIds: string[];
}

export interface EntityQuantityUpdate {
  categoryId: number;
  modelCode: number;
  quantity: number;
  natureId?: number;
}

export interface InterventionUpdateType {
  interventionType: InterventionType;
  hasCustomer?: boolean;
}

export const STANDARD_EXTERNAL_BUSINESS_ID = 2;
export const BUYBACK_BUSINESS_ID = 7;

export enum NotificationStage {
  EMAIL_CONFIRMATION_DONE = 211,
  EMAIL_PRODUCT_AVAILABLE_DONE = 213,
  SMS_PRODUCT_AVAILABLE_DONE = 214,
  EMAIL_NOT_SENT = 215,
  SMS_NOT_SENT = 216,
  EMAIL_UNDELIVERED = 217,
  SMS_UNDELIVERED = 218,
  EMAIL_ERROR = 219,
  SMS_ERROR = 220,
  EMAIL_NOT_FOUND = 221,
  SMS_NOT_FOUND = 222,
  EMAIL_UNKNOWN = 223,
  SMS_UNKNOWN = 224,
  EMAIL_SENDING = 225,
  SMS_SENDING = 226,
}

export enum HistoryStage {
  addService = 111,
  removeService = 112,
  addSparePart = 113,
  removeSparePart = 114,
  quantityChangeService = 115,
  quantityChangeSparePart = 116,
  pogoChangeService = 118,
  pogoChangeSparePart = 119,
  pledgeDateChange = 120,
  editService = 121,
  familyChange = 122,
  businessChange = 123,
}

enum TimeStampType {
  EVENT = 'EVENT',
  NOTIFICATION = 'NOTIFICATION',
  HISTORY = 'HISTORY',
}

export interface InterventionTimestamp {
  timestampId: FullEvent;
  timestampDate: string;
  technician: string;
  type: TimeStampType;
}

export type FullEvent = InterventionsEventsType | NotificationStage | HistoryStage;

export const enum CustomerAlertType {
  NONE = 'NONE',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  BOTH = 'BOTH',
}

type BillingTypeInIcare = number | null;
type CurrentBillingTypeInIcare = number | null;

export interface EntitiesBillingTypeBody {
  /**
   * pogoId null will automatically calculated on patch/post/delete
   */
  pogoId: BillingTypeInIcare;
  /**
   * pogoId null will automatically calculated on patch/post/delete
   */
  currentPogoId: CurrentBillingTypeInIcare;
  quantity: number;
}

export interface ServicesBillingTypeBody extends EntitiesBillingTypeBody {
  serviceModelCode: number;
}
export interface SparePartsBillingTypeBody extends EntitiesBillingTypeBody {
  sparePartCode: number;
  orderNumbers?: number[];
}

export interface ServicePatchPayload {
  contextId: number;
  newPogoId: number;
  currentPogoId: number;
  quantity: number;
}
export interface SparePartPatchPayload {
  contextId: number;
  newPogoId: number;
  currentPogoId: number;
  quantity: number;
  orders?: number[];
}
export interface ServicePostPayload {
  pogoId: number;
  quantity: number;
  serviceModelCode: number;
}
export interface SparePartPostPayload {
  pogoId: number;
  quantity: number;
  sparePartCode: number;
  orderNumbers: number[];
}

export interface PurchaseIntervention {
  documentNumber: string;
  services: {
    itemCode: number;
    label: string;
  }[];
  realisationSite: string;
  type: number;
  creationDate: string;
  totalPrice: number;
  currency: string;
}

export interface FillInterventionsInformationParams {
  interventions: WorkshopIntervention[];
  withoutIdentity?: boolean;
  processes?: FilterType[];
}

export interface ExternalReference {
  externalReferenceType: typeof BOOKING_REFERENCE_TYPE | typeof SKU_REFERENCE_TYPE;
  externalReferenceValue: string;
}

export interface InterventionStorePayload {
  model: {
    isLocal: boolean;
    isWarranty?: boolean | null;
    interventionType: InterventionType;
    contextId?: number;
    family?: number | null;
    storeId: number;
    realisationSite?: number;
    creationSite: number;
    depositedProduct: InterventionProduct;
    availabilityDate?: string | null;
    memberId?: string;
    customerLanguage?: string | null;
    customerAlertType?: CustomerAlertType;
    storeComment: string;
    workshopComment: string;
    traceabilityNumber?: string;
    stringTension?: string;
    externalReferenceNumber?: string;
    externalReference?: ExternalReference;
    files: InterventionFiles;
    bonusRepairCode?: string;
  };
  afterCreation: {
    isHomeService: boolean;
    services: ServicesBillingTypeBody[];
    spareParts: SparePartsBillingTypeBody[];
    priceOptions: StoreIdAndTaxesCondition;
    externalReferences?: ExternalReference[];
  };
}
